import { FormEvent, KeyboardEvent, useId } from "react";
import { Loader2, Send } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

import { cn } from "@/lib/utils";

import { PROMPT_MAX } from "@/schema/brand-os/insights-run";

interface InsightsComposerProps {
  prompt: string;
  onPromptChange: (next: string) => void;
  /** True while the run mutation is in flight. Disables submit + textarea. */
  isPending: boolean;
  onSubmit: () => void;
  /**
   * Slot for the saved-prompts drawer trigger button. Owned by the
   * section so it can wrap the trigger in `<PopoverTrigger asChild>`.
   * Composer just lays it out inside the action row.
   */
  savedSlot?: React.ReactNode;
  /**
   * `true` when the textarea should auto-focus on mount. Used the first
   * time the section comes into view; subsequent mounts skip it so we
   * don't yank the cursor away from elsewhere on the page.
   */
  autoFocus?: boolean;
}

const PLACEHOLDER =
  "Ask anything — e.g. 'What's leaking spend on Meta?', 'Give me an action plan to improve conversions next week', 'Show top performers and what to replicate'.";

/**
 * The single textarea that drives `/insights/run`. Replaces the old
 * "What needs attention" prompt picker entirely.
 *
 * Submit on `Cmd/Ctrl + Enter` (matching the chat surface).
 *
 * The drawer trigger is a passive button — it just opens a popover
 * (managed by the parent) listing the user's saved prompts. There is
 * NO save button on this composer; the backend auto-saves on every
 * successful run.
 *
 * Note: the backend still accepts a `deep_research` param; we just
 * don't expose it in the UI today. If/when we want the toggle back,
 * thread `deepResearch` props through this component and through
 * `useInsightsRun.run({ deep_research })`.
 */
export function InsightsComposer({
  prompt,
  onPromptChange,
  isPending,
  onSubmit,
  savedSlot,
  autoFocus,
}: InsightsComposerProps) {
  const id = useId();
  const trimmed = prompt.trim();
  const tooLong = prompt.length > PROMPT_MAX;
  const canSubmit = trimmed.length > 0 && !tooLong && !isPending;

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (canSubmit) onSubmit();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Cmd/Ctrl+Enter submits — matches the rest of the product. We don't
    // intercept plain Enter because users routinely paste multi-line
    // prompts and an accidental newline shouldn't fire an LLM call.
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      if (canSubmit) onSubmit();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-2.5">
      <div
        className={cn(
          "relative rounded-xl border bg-card shadow-xs transition-colors focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/20",
          tooLong && "border-destructive/60",
        )}
      >
        <Textarea
          id={id}
          value={prompt}
          onChange={(e) => onPromptChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={PLACEHOLDER}
          disabled={isPending}
          autoFocus={autoFocus}
          className="min-h-[88px] resize-y border-0 bg-transparent p-3.5 pb-12 text-sm shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
          aria-invalid={tooLong || undefined}
          aria-describedby={`${id}-helper`}
        />

        {/* Bottom action row — overlaid on the textarea so the visual unit
            reads as one composer rather than separate widgets. */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 px-3 pb-2.5">
          <div className="pointer-events-auto flex items-center gap-3">
            {savedSlot}
          </div>

          <div className="pointer-events-auto flex items-center gap-2">
            <span
              className={cn(
                "text-[11px] tabular-nums",
                tooLong ? "text-destructive" : "text-muted-foreground/70",
              )}
            >
              {prompt.length}/{PROMPT_MAX}
            </span>
            <Button type="submit" size="sm" className="h-7 px-3" disabled={!canSubmit}>
              {isPending ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Send className="size-3.5" />
              )}
              <span className="ml-1.5">Run analysis</span>
            </Button>
          </div>
        </div>
      </div>

      <p id={`${id}-helper`} className={cn("text-[11px]", tooLong ? "text-destructive" : "text-muted-foreground")}>
        {tooLong
          ? `Too long — max ${PROMPT_MAX} characters.`
          : "Cmd+Enter to run · The agent picks insights vs. recommendations from your prompt."}
      </p>
    </form>
  );
}
